<template>
  <BaseCard :heading="filters.capitalize($t('global.items.service', 2))">
    <Flex direction="column">
      <BaseAlert
        v-if="hasDurationWarning"
        color="warning"
        :text="$t('package.duration_warning', { duration: totalDurationText })"
        v-test="'package-duration-warning'"
      />
      <Draggable
        v-model="visibleItems"
        handle=".js-drag-handle"
        item-key="_uid"
        :class="$style.list"
      >
        <template #item="{ element }">
          <div :class="$style.row">
            <div
              v-if="expandedUid === element._uid || !element.serviceId"
              :class="$style.rowExpanded"
            >
              <div :class="$style.rowSelect">
                <BaseDropdown
                  :modelValue="element.serviceId"
                  :options="serviceOptions(element.serviceId)"
                  :groups="categoryGroups"
                  :label="$t('global.items.service', 1)"
                  :placeholder="$t('package.service_placeholder')"
                  searchable
                  required
                  @update:modelValue="onServiceSelected(element, $event)"
                  v-test="'package-service-select'"
                />
              </div>
              <div
                v-if="visibleItems.length > 1"
                :class="$style.rowActions"
              >
                <div
                  :class="$style.rowIcon"
                  @click="removeItem(element)"
                  v-test="'package-service-remove'"
                >
                  <BaseIcon
                    name="close"
                    size="s"
                  />
                </div>
                <div :class="[$style.rowIcon, 'js-drag-handle']">
                  <BaseIcon
                    name="drag"
                    size="s"
                  />
                </div>
              </div>
            </div>
            <div
              v-else
              :class="$style.rowCollapsed"
              @click="expandedUid = element._uid"
              v-test="'package-service-row'"
            >
              <div :class="$style.rowName">
                <BaseText oneLine>
                  {{ serviceLabel(element.serviceId) }}
                </BaseText>
              </div>
              <div :class="$style.rowDuration">
                <BaseText color="secondary">
                  {{ durationLabel(element.serviceId) }}
                </BaseText>
              </div>
              <div :class="$style.rowIcon">
                <BaseIcon
                  name="edit"
                  size="s"
                />
              </div>
              <div
                v-if="visibleItems.length > 1"
                :class="[$style.rowIcon, 'js-drag-handle']"
              >
                <BaseIcon
                  name="drag"
                  size="s"
                />
              </div>
            </div>
          </div>
        </template>
      </Draggable>
      <BaseText
        link
        @click="addItem"
        v-test="'package-add-service'"
      >
        + {{ $t('package.add_service') }}
      </BaseText>
    </Flex>
  </BaseCard>
</template>

<script
  setup
  lang="ts"
>
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import filters from '@/filters';
import { useServiceGroupsStore } from '@/stores/service-groups';
import { useServicesStore } from '@/stores/services';
import { formatServiceLabel } from './serviceLabel';
import type { PackageServiceItem } from './types';

// A package longer than a typical 9-hour working day can never be booked in a single day.
const WORKDAY_WARN_MINUTES = 9 * 60;

const { t } = useI18n();

const items = defineModel<PackageServiceItem[]>({ required: true });

const { serviceGroups } = storeToRefs(useServiceGroupsStore());
const { serviceCategories } = storeToRefs(useServicesStore());

let uidCounter = 0;
const nextUid = () => {
  uidCounter -= 1;
  return uidCounter;
};

const visibleItems = computed<PackageServiceItem[]>({
  get: () => items.value.filter((item) => !item.destroy),
  set: (newOrder) => {
    items.value = [...newOrder, ...items.value.filter((item) => item.destroy)];
  },
});

const expandedUid = ref<number | null>(null);

const flattenedServices = computed(() =>
  serviceGroups.value.flatMap((group) =>
    group.services.map((service) => ({
      id: service.id,
      categoryId: group.category.id,
      price: service.price,
      duration: service.duration,
      label: formatServiceLabel(group, service),
    })),
  ),
);

const serviceById = computed(
  () =>
    new Map(flattenedServices.value.map((service) => [service.id, service])),
);

const selectedServiceIds = computed(
  () =>
    new Set(
      visibleItems.value
        .map((item) => item.serviceId)
        .filter((id): id is number => id != null),
    ),
);

const serviceOptions = (currentServiceId: number | null) =>
  flattenedServices.value
    .filter(
      (service) =>
        service.id === currentServiceId ||
        !selectedServiceIds.value.has(service.id),
    )
    .map((service) => ({
      value: service.id,
      label: service.label,
      groupValue: service.categoryId,
      labelExtra: filters.currency(service.price),
    }));

const categoryGroups = computed(() =>
  serviceCategories.value.map((category) => ({
    value: category.id,
    label: category.name,
  })),
);

const serviceLabel = (serviceId: number | null) =>
  serviceId != null ? (serviceById.value.get(serviceId)?.label ?? '') : '';

const durationLabel = (serviceId: number | null) => {
  const duration =
    serviceId != null ? serviceById.value.get(serviceId)?.duration : null;
  return duration != null
    ? `${duration} ${t('global.items.minute_short', duration)}`
    : '';
};

const totalDuration = computed(() =>
  visibleItems.value.reduce(
    (sum, item) =>
      sum +
      (item.serviceId != null
        ? (serviceById.value.get(item.serviceId)?.duration ?? 0)
        : 0),
    0,
  ),
);

const totalDurationText = computed(
  () =>
    `${totalDuration.value} ${t('global.items.minute', totalDuration.value)}`,
);

const hasDurationWarning = computed(
  () => totalDuration.value > WORKDAY_WARN_MINUTES,
);

const onServiceSelected = (item: PackageServiceItem, serviceId: number) => {
  item.serviceId = serviceId;
  item.originalPrice = serviceById.value.get(serviceId)?.price ?? null;
  expandedUid.value = null;
};

const addItem = () => {
  const item: PackageServiceItem = {
    _uid: nextUid(),
    serviceId: null,
    originalPrice: null,
  };
  items.value = [...items.value, item];
  expandedUid.value = item._uid;
};

const removeItem = (item: PackageServiceItem) => {
  if (item.id) {
    item.destroy = true;
  } else {
    items.value = items.value.filter((i) => i._uid !== item._uid);
  }
};

watchEffect(() => {
  if (!items.value.some((item) => !item.destroy)) {
    addItem();
  }
});
</script>

<style
  lang="scss"
  module
>
.list {
  display: flex;
  flex-direction: column;
  gap: $spacing * 0.5;
}

.row {
  width: 100%;
  border: 1px solid var(--color-border);
  border-radius: $radius;
  background-color: var(--color-bg-secondary);
  box-sizing: border-box;
}

.rowExpanded {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: $spacing * 0.75 $spacing;
  gap: $spacing * 0.5;
}

.rowSelect {
  min-width: 0;
  flex-grow: 1;
}

.rowActions {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
}

.rowCollapsed {
  display: flex;
  width: 100%;
  align-items: center;
  gap: $spacing * 0.75;
  padding: $spacing * 0.75 $spacing;
  cursor: pointer;
}

.rowName {
  flex-grow: 1;
  min-width: 0;
}

.rowDuration {
  flex-shrink: 0;
}

.rowIcon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &.js-drag-handle {
    cursor: grab;
  }
}
</style>
